import type { CSSProperties, ReactElement, ReactNode } from "react";
import { palette, space } from "@ux/tokens";

export interface BottomActionBarProps {
    /**
     * Children render right-to-left in priority order: the rightmost
     * child is the primary action (typically Save), with secondary and
     * tertiary actions to its left. Pages should `Stack` children into
     * the desired ordering before passing them in — this composition
     * does no layout magic on the children themselves.
     */
    readonly children: ReactNode;
}

/**
 * Sticky bottom action bar used by detail/form views on phones to keep
 * the primary action (Save, Done) inside the thumb zone instead of
 * stranded at the top of a long-scrolled form.
 *
 * Design constraints baked in:
 *
 *   - Sticky to the bottom of the page area, not fixed to the viewport
 *     — the parent flex column already guarantees the bar lands where
 *     the user expects, and sticky degrades more gracefully than fixed
 *     when the AdminShell decides to also pin a tab bar below it.
 *
 *   - Bottom padding accounts for `env(safe-area-inset-bottom)` so the
 *     bar clears the iOS home indicator. Using `max(...)` against a
 *     base spacing token guarantees a minimum visual gap even on
 *     devices that report a zero inset (Android Chrome with on-screen
 *     gesture nav, most desktops at narrow widths).
 *
 *   - Top border + raised surface tone preserves the visual separation
 *     between scrolling content and pinned chrome.
 *
 * This composition is phone-first by intent, but it makes no viewport
 * decisions itself — pages call `useViewport().isPhone` and conditionally
 * render the bar. Keeping the responsive switch at the call site means
 * the bar is also reusable in any future mobile-modal context without
 * fighting the primitive.
 */
export function BottomActionBar({ children }: BottomActionBarProps): ReactElement {
    const style: CSSProperties = {
        position: "sticky",
        bottom: 0,
        zIndex: 5,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: space[2],
        padding: `${space[3]} ${space[4]}`,
        paddingBottom: `max(${space[3]}, env(safe-area-inset-bottom))`,
        background: palette.surface,
        borderTop: `1px solid ${palette.border}`,
    };
    return <div style={style}>{children}</div>;
}
